import { type ImageStyle, StyleSheet, type TextStyle, type ViewStyle } from '../web';
import { type Theme, themeMap } from './theme';
import type { DynamicTokens } from './tokens';
import { forEachVariants, type VariantsValue } from './variants';

/**
 * Typing for styles that are specific to the web platform
 */
export type WebSpecificStyle = {
    outline?: string;
    outlineWidth?: number;
    outlineStyle?: string;
    outlineColor?: string;
    outlineOffset?: number;
    position?: 'sticky' | 'fixed';
    cursor?: 'default' | 'text' | 'pointer' | 'grab' | 'grabbing';
};

export type Styles = WebSpecificStyle | ViewStyle | TextStyle | ImageStyle;

export type ThemedStyles = Styles | ((theme: Theme, variantsValue: VariantsValue) => Styles);

export type ThemedNamedStyles = { [key: string]: ThemedStyles };

type ResolveStyle<T extends ThemedStyles> = T extends (
    theme: Theme,
    variantsValue: VariantsValue
) => Styles
    ? ReturnType<T>
    : T;

/**
 * Style sheet resolved with the dynamic tokens.
 * That's what the components consume with `useCurrentTheme`.
 */
export type ResolvedThemedStyleSheet<T extends ThemedNamedStyles> = {
    [styleName in keyof T]: ResolveStyle<T[styleName]>;
};

/** Style sheet resolved before the dynamic tokens */
export type MaybeDynamicStyle =
    | Styles
    | [
          Styles,
          /** Resolver depending on dynamic tokens */
          (dynamicTokens: DynamicTokens) => Styles,
          /** Cache of resolution depending on dynamic tokens */
          Map<string, Styles>,
      ];
type ResolvedStaticThemedStyleSheet<T> = {
    [styleName in keyof T]: MaybeDynamicStyle;
};
export type ThemedStyleSheet<T> = {
    [key: string]: ResolvedStaticThemedStyleSheet<T>;
};

type DynamicNamedStyles<T> = {
    [P in keyof T]: Styles | ((theme: Theme, variantsValue: VariantsValue) => Styles);
};

/**
 * Creates a StyleSheet style reference from the given object.
 */
export function create<T extends DynamicNamedStyles<T> | DynamicNamedStyles<any>>(styles: T) {
    // @ts-ignore
    const result: ThemedStyleSheet<T> = {};

    Object.keys(styles).forEach((key) => {
        const style = styles[key];
        if (typeof style === 'function') {
            forEachVariants((variant, themeKey) => {
                if (!result[themeKey]) {
                    // @ts-ignore
                    result[themeKey] = {};
                }

                const resolvedStyle = style(themeMap.get(themeKey), variant);

                // Some properties can require a dynamic token so we can't resolve them statically.
                // So we are splitting the static properties from the dynamics.
                const [staticStyle, dynamicStyle] = Object.keys(resolvedStyle).reduce(
                    ([staticStyle, dynamicStyle], k) => {
                        if (typeof resolvedStyle[k] === 'function') {
                            dynamicStyle[k] = resolvedStyle[k];
                        } else {
                            staticStyle[k] = resolvedStyle[k];
                        }
                        return [staticStyle, dynamicStyle];
                    },
                    [{}, {}]
                );

                // We generate a static StyleSheet for the static properties 🏎
                const { id } = StyleSheet.create({ id: staticStyle });

                if (!Object.keys(dynamicStyle).length) {
                    // if we don't have any dynamic style, we are all set
                    result[themeKey][key] = id;
                } else {
                    // Cache to store dynamic styles depending on dynamic tokens
                    const dynamicTokensCache = new WeakMap<
                        DynamicTokens,
                        ViewStyle | TextStyle | ImageStyle
                    >();

                    // We generate a single function to resolve all the dynamics properties 🚜
                    result[themeKey][key] = [
                        id,
                        (dynamicTokens: DynamicTokens) => {
                            const existing = dynamicTokensCache.get(dynamicTokens);
                            if (existing) {
                                return existing;
                            }

                            const styles = Object.keys(dynamicStyle).reduce((prev, k) => {
                                prev[k] = dynamicStyle[k](dynamicTokens);
                                return prev;
                            }, {});

                            const { id } = StyleSheet.create({
                                id: styles,
                            });

                            dynamicTokensCache.set(dynamicTokens, id);

                            return id;
                        },
                    ];
                }
            });
        } else {
            // If the styles doesn't require the theme, then we can generate
            // a single StyleSheet for all the theme variants
            // vroom vroom 🚀
            const { id } = StyleSheet.create({ id: style });
            forEachVariants((_variant, themeKey) => {
                if (!result[themeKey]) {
                    // @ts-ignore
                    result[themeKey] = {};
                }
                result[themeKey][key] = id;
            });
        }
    });

    return result;
}

/**
 * Utility to use tokens in a template string.
 * Some tokens are dynamic, and so doesn't play well when used in a template string
 * (they are a function so the function is transformed into a string)
 * Prefixing the template string with this function makes sure that the theme resolution
 * knows what it's dealing with.
 *
 * @example
 * ThemedStyleSheet.create({
 *   style: theme => ({
 *     backgroundImage: ThemedStyleSheet.template`linear-gradient(145deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.standout} 100%)`
 *   })
 * })
 */
export function template(
    strings: TemplateStringsArray,
    ...tokens: (string | number | ((dynamicTokens: DynamicTokens) => string | number))[]
): string {
    if (tokens.every((token) => typeof token !== 'function')) {
        const result: string[] = [strings[0]];
        tokens.forEach((token, i) => {
            result.push(`${token}`, strings[i + 1]);
        });
        return result.join('');
    }

    // @ts-ignore
    return (dynamicTokens: DynamicTokens) => {
        const result: string[] = [strings[0]];
        tokens.forEach((token, i) => {
            result.push(
                typeof token !== 'function' ? `${token}` : `${token(dynamicTokens)}`,
                strings[i + 1]
            );
        });
        return result.join('');
    };
}
